import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AngleDown } from '../svg/angleDownSolid';
import { Icon } from '../icon';
import { Radio } from '../radio';
import { Checkbox } from '../checkbox';
import { TextButton } from '../buttons';
import { useClickOutside } from '../hooks/useClickOutside';

type AllowedPosition = 'LEFT' | 'RIGHT';

const Container = styled.div<{ $disabled?: boolean }>`
    border: 1px solid rgb(222, 222, 222);
    border-radius: 4px;
    white-space: nowrap;
    position: relative;
    background-color: #fff;
    opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
    pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

    @media screen and (max-width: 765px) {
        width: auto;
        margin: 0 10px;
    }

    &:hover {
        background-color: #f6f8f9;
        cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    }
`;

const Label = styled.div<{ $width?: string }>`
    user-select: none; /* Standard */
    align-self: center;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    padding: 0 1em;
    height: 40px;
    box-sizing: border-box;
    ${(props) => (props.$width ? `width: ${props.$width};` : 'width: auto;')}

    > span {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    @media screen and (max-width: 765px) {
        padding: 0 10px;
    }
`;

const StyledIcon = styled(Icon)`
    width: 13px;
    height: 13px;
`;

const Popover = styled.div<{ $width?: string; $position?: AllowedPosition }>`
    margin: 0 auto;
    z-index: 2;
    position: absolute;
    width: auto;
    ${(props) => props.$width && `min-width: ${props.$width};`}
    ${(props) => (!props.$position || props.$position === 'LEFT') && `left: 0;`}
  ${(props) => props.$position && props.$position === 'RIGHT' && `right: 0;`}
  top: calc(100% + 5px);

    [class*='StyledDefault'] {
        margin: 5px 0 5px 0 !important;
    }
`;

const FilterRadio = styled(Radio as typeof Radio<string>)`
    label {
        font-size: 14px;
    }
`;

const PopoverBody = styled.div`
    padding-left: 1em;
    padding-right: 1em;
    border: 1px solid;
    border-radius: 5px;
    border-color: rgb(222, 222, 222);
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    box-shadow:
        rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
        rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;

    &:first-child {
        padding-top: 1rem;
    }

    & > * {
        cursor: pointer;
    }

    &:last-child {
        padding-bottom: 1rem;
    }
`;

const List = styled.div<{ $maxHeight?: string }>`
    ${(props) => props.$maxHeight && `max-height: ${props.$maxHeight}; overflow-y: scroll;`}
`;

const ListItem = styled.div`
    list-style: none;
    background-color: #fff;
    width: 100%;
    display: flex;
    flex-direction: row;

    > * {
        background-color: #fff;
        cursor: pointer;
        -webkit-user-select: none; /* Safari */
        -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* IE10+/Edge */
        user-select: none; /* Standard */
    }
`;

const TextButtonStyled = styled(TextButton)`
    margin-top: 1rem;
`;

const StyledCheckboxLabel = styled.span`
    align-self: center;
    font-size: 14px;
`;

interface IOption<T> {
    label: string | React.ReactNode;
    value: T;
}

export type SelectionValue<T> = T | T[] | null;

export interface ISelectOptionsProps<T> {
    options: IOption<string>[];
    placeholder: string;
    value?: SelectionValue<T>;
    onChange?: (value: SelectionValue<T>) => void;
    width?: string;
    maxHeight?: string;
    position?: AllowedPosition;
    multiple?: boolean;
    clearable?: boolean;
    updateLabel?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
}

export const SelectOptions = <T = string,>({
    value,
    options,
    placeholder,
    width,
    position,
    onChange,
    maxHeight,
    multiple = true,
    clearable = true,
    updateLabel = false,
    disabled = false,
    disabledTooltip = ''
}: ISelectOptionsProps<T>) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    useClickOutside(popoverRef, () => setIsVisible(false), containerRef);

    const [_innerState, _setInnerState] = useState<SelectionValue<T>>([]);
    const [isVisible, setIsVisible] = useState<boolean>(false);

    useEffect(() => {
        const normalizedValue = Array.isArray(value) ? value.map((v) => String(v)) : value != null ? String(value) : multiple ? [] : null;

        const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);
        if (!isEqual(_innerState, normalizedValue)) {
            _setInnerState(normalizedValue as SelectionValue<T>);
        }
    }, [value]);

    const handleInputChange = (value: T, remove = false) => {
        if (!multiple) {
            onChange?.(value);
            return _setInnerState(value);
        }

        // we should not call onChange in this function since it should remain pure,
        // however we are trying to not add dependencies on the _internalState in order
        // to avoid possible infinite loops.
        _setInnerState((current) => {
            if (!Array.isArray(current)) {
                onChange?.(value);
                return value;
            }

            if (remove) {
                const index = current.indexOf(value);
                if (index === -1) return current;
                const tempCurrent = [...current];
                tempCurrent.splice(index, 1);
                const updated = [...tempCurrent];
                onChange?.(updated);
                return updated;
            }

            const updated = [...current, value];
            onChange?.(updated);
            return updated;
        });
    };

    const handleClearing = () => {
        _setInnerState(multiple ? [] : null);
        onChange?.(multiple ? [] : null);
    };

    const label = useMemo(() => {
        if (!updateLabel || !_innerState || (Array.isArray(_innerState) && _innerState.length === 0)) return placeholder;

        if (!multiple) return options.find((o) => o.value === _innerState)?.label ?? placeholder;

        return (_innerState as string[])
            .map((val) => options.find((o) => o.value === val)?.label)
            .filter(Boolean)
            .join(', ');
    }, [_innerState, placeholder, updateLabel]);

    const SelectContent = (
        <Container ref={containerRef} $disabled={disabled}>
            <Label
                $width={width}
                onClick={(ev): void => {
                    if (disabled) return;
                    ev.stopPropagation();
                    setIsVisible((v) => !v);
                }}
            >
                <span>{label}</span>
                <StyledIcon>
                    <AngleDown />
                </StyledIcon>
            </Label>
            {isVisible && (
                <Popover $width={width} $position={position} ref={popoverRef}>
                    <PopoverBody>
                        <List $maxHeight={maxHeight}>
                            {options.map((option, index) => (
                                <ListItem key={`option-${option.value.replaceAll(' ', '')}-${index}`}>
                                    {multiple ? (
                                        <Checkbox
                                            checked={Array.isArray(_innerState) && _innerState.includes(option.value as T)}
                                            onChange={(checked) => handleInputChange(option.value as T, !checked)}
                                            label={<StyledCheckboxLabel>{option.label}</StyledCheckboxLabel>}
                                        />
                                    ) : (
                                        <FilterRadio
                                            name='type'
                                            label={option.label}
                                            value={option.value}
                                            checked={_innerState === option.value}
                                            onSelect={(val: string | number) => handleInputChange(val as T)}
                                        />
                                    )}
                                </ListItem>
                            ))}
                        </List>
                        {clearable && <TextButtonStyled onClick={handleClearing}>Clear</TextButtonStyled>}
                    </PopoverBody>
                </Popover>
            )}
        </Container>
    );

    return disabled && disabledTooltip ? <div title={disabledTooltip}>{SelectContent}</div> : SelectContent;
};
